import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { ChartCard } from "./ChartCard";
import { getDueDateBucket } from "@/data/opportunities";

const URGENCY_COLORS = {
  "Due in 7 days": "hsl(0, 72%, 51%)",
  "Due in 14 days": "hsl(38, 92%, 50%)",
  "Due in 30 days": "hsl(162, 73%, 35%)",
  "30+ days": "hsl(222, 47%, 35%)",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg px-4 py-3">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm text-muted-foreground mt-1">
          <span className="font-semibold text-foreground">{payload[0].value}</span> opportunities
        </p>
      </div>
    );
  }
  return null;
};

export const DueDateChart = ({ data }) => {
  const chartData = useMemo(() => {
    const buckets = {
      "Due in 7 days": 0,
      "Due in 14 days": 0,
      "Due in 30 days": 0,
      "30+ days": 0,
    };

    data.forEach(opp => {
      const bucket = getDueDateBucket(opp.due);
      buckets[bucket]++;
    });

    return Object.entries(buckets).map(([name, count]) => ({
      name,
      count,
    }));
  }, [data]);

  return (
    <ChartCard 
      title="Pipeline by Due Date" 
      description="Opportunities organized by submission deadline"
    >
      <div className="h-[280px] mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData}
            margin={{ top: 20, right: 20, left: 10, bottom: 5 }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false}
              stroke="hsl(var(--border))"
            />
            <XAxis 
              dataKey="name" 
              tick={{ fill: 'hsl(var(--foreground))', fontSize: 11, fontWeight: 500 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis 
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
            <Bar 
              dataKey="count" 
              radius={[6, 6, 0, 0]}
              maxBarSize={60}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={URGENCY_COLORS[entry.name]} />
              ))}
              <LabelList 
                dataKey="count" 
                position="top" 
                fill="hsl(var(--foreground))"
                fontSize={12}
                fontWeight={600}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};
